import React, { useEffect, useMemo } from "react";
import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  Cell,
  SpreadsheetTemplate,
} from "@vrf/fr-vrf-spreadsheet-core-templating";
import {
  FrVrfActionList,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFileUploadField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfSingleLineDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { unloadExcel } from "@vrf/fr-vrf-unload-excel-core";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import {
  useGetRoleCodeList,
  useGetGeneralCodes,
} from "../../../services/data-list";
import {
  useUpdateProjectStakeholders,
  useDeleteProjectStakeholders,
} from "../../../services/project";
import {
  InDefCommonFlagType,
  RoleType,
} from "../../../util/general-code/enums/general-codes";
import {
  InputStakeholderDialogContent,
  InputStakeholderDialogInitialState,
  useInputStakeholderDialogState,
} from "../input-stakeholder-dialog";
import {
  useProjectStakeholderListState,
  Option,
  SpreadsheetRecordData,
  LoadedRecordData,
  CellItem,
  ProjectStakeholderListInitialState,
  SpreadsheetState,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { mappingErrorResponseToSpreadsheet } from "../../../util";
import _ from "lodash";

export enum DisplayFilterType {
  All = "1",
  Project = "2",
  Dip = "3",
}

const TEMPLATE_FILE_URL = `spreadsheet-template/project-stakeholder-list.xlsx`;
const TEMPLATE_FILE_URL_LOAD = `spreadsheet-template/project-stakeholder-list-load.xlsx`;
const MANUAL_LINK_LIST = [
  {
    label: "1. Create Project",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=793717328",
  },
  {
    label: "2. Assign Requirements Definer",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=847035100",
  },
  {
    label: "8. Assign Stakeholders ~ Start Project",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=817541429",
  },
];

export const ProjectStakeholderListContent: React.FC = () => {
  const { showToast, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getRoleCodeListResponse, getRoleCodeListRequest] =
    useGetRoleCodeList();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const [updateProjectStakeholdersResponse, updateProjectStakeholdersRequest] =
    useUpdateProjectStakeholders();
  const [deleteProjectStakeholdersResponse, deleteProjectStakeholdersRequest] =
    useDeleteProjectStakeholders();
  const [inputStakeholderDialogState, setInputStakeholderDialogState] =
    useInputStakeholderDialogState();
  const [pageState, setPageState] = useProjectStakeholderListState();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();
  const { moveToModalPage, closeModalPage } = usePageNavigation();

  const closeModal = (isSaved: boolean) => {
    if (!isSaved) {
      resetUserError();
      setPageState(ProjectStakeholderListInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      status: {
        ...prevState.status,
        isSaved: true,
      },
    }));
  };

  const displayFilter = (
    spreadsheetRecordData: SpreadsheetRecordData[],
    filter: String,
  ) => {
    if (pageState.fromActionList) {
      return spreadsheetRecordData.filter(
        (e) => e.roleCd === inputStakeholderDialogState.edited.roleCd,
      );
    }

    if (filter === DisplayFilterType.Dip) {
      return spreadsheetRecordData.filter((e) =>
        [
          RoleType.DIP_MANAGER,
          RoleType.DIP_APPROVER,
          RoleType.PIC_OF_DIP_APP,
          RoleType.PIC_OF_DIP_INFRA,
          RoleType.PIC_OF_IF_RD,
          RoleType.PROJECT_OWNER,
          RoleType.PROJECT_APPROVER,
          RoleType.PIC_OF_COMPONENT_APP,
          RoleType.PIC_OF_COMPONENT_INFRA,
        ].includes(e.roleCd as RoleType),
      );
    }

    if (filter === DisplayFilterType.Project) {
      return spreadsheetRecordData.filter((e) =>
        [
          RoleType.PROJECT_OWNER,
          RoleType.PROJECT_APPROVER,
          RoleType.PIC_OF_COMPONENT_APP,
          RoleType.PIC_OF_COMPONENT_INFRA,
        ].includes(e.roleCd as RoleType),
      );
    }

    return spreadsheetRecordData;
  };

  const requestReloadProjectInfo = () => {
    setPageState((prevState) => ({
      ...prevState,
      reqReload: true,
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    const cdTypes = ["in_def_common_flag_type"];
    getRoleCodeListRequest({});
    getGeneralCodesRequest({ cdTypes: cdTypes });
  };
  useEffect(onInitialDisplayed, []);

  const onGetRoleCodeListResponse = () => {
    if (getRoleCodeListResponse === undefined) return;

    const getFilteredRoleCodeList = (filter: String) => {
      if (filter === DisplayFilterType.Dip) {
        return getRoleCodeListResponse.roleCodeList.filter((e) =>
          [
            RoleType.DIP_MANAGER,
            RoleType.DIP_APPROVER,
            RoleType.PIC_OF_DIP_APP,
            RoleType.PIC_OF_DIP_INFRA,
            RoleType.PIC_OF_IF_RD,
            RoleType.PROJECT_OWNER,
            RoleType.PROJECT_APPROVER,
            RoleType.PIC_OF_COMPONENT_APP,
            RoleType.PIC_OF_COMPONENT_INFRA,
          ].includes(e.value as RoleType),
        );
      }
      if (filter === DisplayFilterType.Project) {
        return getRoleCodeListResponse.roleCodeList.filter((e) =>
          [
            RoleType.PROJECT_OWNER,
            RoleType.PROJECT_APPROVER,
            RoleType.PIC_OF_COMPONENT_APP,
            RoleType.PIC_OF_COMPONENT_INFRA,
          ].includes(e.value as RoleType),
        );
      }
      return getRoleCodeListResponse.roleCodeList.filter(
        (e) => ![RoleType.GOD, RoleType.SYSTEM].includes(e.value as RoleType),
      );
    };

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        roleCdList: getFilteredRoleCodeList(prevState.displayFilter).map(
          (e) => ({
            label: e.label,
            value: e.value,
            cvrgType: e.cvrgType,
          }),
        ),
      },
    }));
  };
  useEffect(onGetRoleCodeListResponse, [getRoleCodeListResponse]);

  const ongetGetGeneralCodes = () => {
    let inDefCommonFlagTypeCd = [] as Option[];
    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "in_def_common_flag_type":
          inDefCommonFlagTypeCd = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        inDefCommonFlagTypeCd: inDefCommonFlagTypeCd,
      },
    }));
  };
  useEffect(ongetGetGeneralCodes, [getGeneralCodesResponse]);

  const spreadsheetInitialData = useMemo(() => {
    if (pageState.edited === undefined) return;

    const spreadsheetRecordData: SpreadsheetRecordData[] = pageState.edited.map(
      (e) => {
        return {
          ...ProjectStakeholderListInitialState.spreadsheet.tmplateRow,
          ...e,
        };
      },
    );

    const filteredSpreadsheetRecordData = displayFilter(
      spreadsheetRecordData,
      pageState.displayFilter,
    );

    return {
      results: filteredSpreadsheetRecordData,
      codes: {
        inDefCommonFlagTypeCd: pageState.codes.inDefCommonFlagTypeCd,
      },
    };
  }, [pageState.edited, pageState.codes.inDefCommonFlagTypeCd]);

  /**
   * Input Stakeholder Dialog.
   */
  const showInputStakeholderDialog = (rowIdx: number) => {
    if (spreadsheetState === undefined) return;

    setInputStakeholderDialogState({
      ...inputStakeholderDialogState,
      saved: { ...spreadsheetState.results[rowIdx] },
      edited: { ...spreadsheetState.results[rowIdx] },
      displayFilter: pageState.displayFilter,
      fromActionList: pageState.fromActionList,
      status: {
        ...inputStakeholderDialogState.status,
      },
      rowIdx: rowIdx,
      isShow: true,
    });
  };

  const onListenUpdateStakeholder = (): void => {
    if (
      inputStakeholderDialogState.isShow === true ||
      inputStakeholderDialogState.status.isUpdated === false
    )
      return;

    const editedSpreadsheetRow = JSON.parse(
      JSON.stringify(inputStakeholderDialogState.edited),
    );
    editedSpreadsheetRow.updateFlag = true;

    if (
      spreadsheetState?.results
        .filter((e) => e.primFlg === InDefCommonFlagType.YES)
        .some(
          (e) =>
            e.roleCd === editedSpreadsheetRow.roleCd &&
            e.teamCd === editedSpreadsheetRow.teamCd,
        )
    ) {
      editedSpreadsheetRow.primFlg = InDefCommonFlagType.NO;
    }

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    if (inputStakeholderDialogState.requireRow) {
      newSpreadsheetRecordData.splice(
        inputStakeholderDialogState.rowIdx,
        0,
        editedSpreadsheetRow,
      );
    } else {
      newSpreadsheetRecordData[inputStakeholderDialogState.rowIdx] = {
        ...editedSpreadsheetRow,
      };
    }

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });

    setInputStakeholderDialogState({
      ...InputStakeholderDialogInitialState,
      saved: { ...ProjectStakeholderListInitialState.spreadsheet.tmplateRow },
      edited: { ...ProjectStakeholderListInitialState.spreadsheet.tmplateRow },
      requireRow: false,
    });
  };
  useEffect(onListenUpdateStakeholder, [inputStakeholderDialogState.isShow]);

  const onCellEditedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const value: string = event.detail.cell.value ?? "";
    const isChangedRow: boolean = event.detail.cell._row.isChanged;
    const name = event.detail.name;
    const selectedRow = event.detail.value;
    const isValid = event.detail.cell.valid;
    const cellItem: CellItem = {
      rowIdx: rowIdx,
      col: name,
    };

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    switch (name) {
      case "updateFlag":
        newSpreadsheetRecordData[rowIdx].updateFlag = !selectedRow.updateFlag;
        break;
      case "isPrimary":
        newSpreadsheetRecordData[rowIdx].primFlg = value;
        newSpreadsheetRecordData[rowIdx].updateFlag = isChangedRow;
        break;
    }

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });

    setPageState((prevState) => ({
      ...prevState,
      status: {
        ...prevState.status,
        invalidRecordList: isValid
          ? prevState.status.invalidRecordList.filter(
              (e) => e !== JSON.stringify(cellItem),
            )
          : prevState.status.invalidList.concat(JSON.stringify(cellItem)),
      },
    }));
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newStakeHolderList = _.cloneDeep(
      spreadsheetState?.results,
    ) as SpreadsheetRecordData[];

    const colName: string = event.detail.name;
    if (colName !== "updateFlag" && colName !== "isPrimary") return;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;
      if (colName === "isPrimary") {
        newStakeHolderList[rowIdx].updateFlag = true;
        newStakeHolderList[rowIdx].primFlg = value;
      } else {
        newStakeHolderList[rowIdx].updateFlag = value === "true";
      }
    });

    setSpreadsheetState({
      ...spreadsheetState,
      results: newStakeHolderList,
    });
  };

  const onCellClickedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const name = event.detail.name;

    if (name === "editButton") {
      showInputStakeholderDialog(rowIdx);
      return;
    }

    if (name !== "deleteButton") return;
    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    newSpreadsheetRecordData.splice(rowIdx, 1);
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });
  };

  const onCellSelectedHandle = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheet: {
        ...prevState.spreadsheet,
        selectRowIdx: rowIndex,
      },
    }));
  };

  const onClickUnload = () => {
    if (!spreadsheetState) return;

    const records = spreadsheetState.results.map((e) => {
      return {
        ...e,
        primFlgLabel: e.primFlg === InDefCommonFlagType.YES ? "Yes" : "No",
      };
    });

    const fileName = "project-stakeholder-list.xlsx";
    const sheetName = "data";
    const data = { records: records };
    const mapRule = {
      records: "records",
      columns: [
        {
          header: "Primary",
          key: "primFlgLabel",
          style: { bgColor: "--vrf-spreadsheet-theme-required-bgcolor" },
        },
        {
          header: "Team Code",
          key: "teamCd",
          style: { bgColor: "--vrf-spreadsheet-theme-required-bgcolor" },
        },
        {
          header: "Role Name",
          key: "roleName",
          style: { bgColor: "--vrf-spreadsheet-theme-required-bgcolor" },
        },
        {
          header: "User ID",
          key: "userId",
          style: { bgColor: "--vrf-spreadsheet-theme-required-bgcolor" },
        },
        { header: "User Name", key: "userName" },
        { header: "Mail Address", key: "mlAdrs" },
        {
          header: "Note",
          key: "stkhldrNote",
          style: { bgColor: "--vrf-spreadsheet-theme-optional-bgcolor" },
        },
        {
          header: "Stakeholder ID",
          key: "stkhldrId",
        },
      ],
    };
    unloadExcel(fileName, data, mapRule, sheetName);
  };

  const onClickLoad = async (event: CustomEvent): Promise<void> => {
    const file = event.detail.file as File;
    let loadedList: LoadedRecordData[] = [];

    try {
      const data: any = await new SpreadsheetTemplate(
        TEMPLATE_FILE_URL_LOAD,
      ).loadDataFile(file);
      loadedList = data;
    } catch (e) {
      console.log(e);
    }

    const blankRowFilterdList = loadedList.filter(
      (row) =>
        row.primFlg !== "" ||
        row.roleName !== "" ||
        row.stkhldrId !== "" ||
        row.stkhldrNote !== "" ||
        row.teamCd !== "" ||
        row.userId !== "",
    );

    // Alert if none of the loaded rows exist.
    if (blankRowFilterdList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00125,
          "Data",
        ),
      });
      return;
    }

    // Alert if StakeholderID is duplicated.
    const loadedstakeholderIdList = blankRowFilterdList
      .filter((e) => e.stkhldrId !== "")
      .map((e) => e.stkhldrId);
    const setloadedstakeholderIdList = new Set(loadedstakeholderIdList);
    if (setloadedstakeholderIdList.size !== loadedstakeholderIdList.length) {
      showToast({
        design: "alert",
        message:
          "Stakeholder ID " +
          messageForScreen(ApplicationMessageScreen.APP_SCN_00040),
      });
      return;
    }

    // Alert if the login user specifies a role code that does not have the specified authority.
    const roleNameList = pageState.codes.roleCdList?.map((e) => e.label) ?? [];
    const invalidList = blankRowFilterdList.filter(
      (e) => !roleNameList.includes(e.roleName),
    );
    if (invalidList.length > 0) {
      showToast({
        design: "alert",
        message: "The specified Role Name is invalid.",
      });
      return;
    }

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = [];

    // Update and Add loaded Row.
    blankRowFilterdList.forEach((loadedRow) => {
      if (
        loadedRow["stkhldrId"] !== "" &&
        spreadsheetState?.results.some(
          (e) => e.stkhldrId === loadedRow["stkhldrId"],
        )
      ) {
        const rowIdx = spreadsheetState?.results.findIndex(
          (e) => e.stkhldrId === loadedRow["stkhldrId"],
        );
        let targetRow = JSON.parse(
          JSON.stringify(spreadsheetState?.results[rowIdx]),
        );
        const targetRowOrg = JSON.stringify(spreadsheetState?.results[rowIdx]);

        const roleCd = pageState.codes.roleCdList?.find(
          (e) => loadedRow["roleName"] === e.label,
        )?.value;
        const primFlg =
          pageState.codes.inDefCommonFlagTypeCd?.find(
            (e) => loadedRow["primFlg"] === e.label,
          )?.value ?? "";
        targetRow.isAddRow = false;
        targetRow.primFlg = primFlg;
        targetRow.roleName = roleCd ? loadedRow["roleName"] : "";
        targetRow.roleCd = roleCd ?? "";
        targetRow.teamCd = loadedRow["teamCd"];
        targetRow.stkhldrNote = loadedRow["stkhldrNote"];
        if (targetRow.userId !== loadedRow["userId"]) {
          targetRow.userId = loadedRow["userId"];
          targetRow.userName = "";
          targetRow.mlAdrs = "";
        }
        if (targetRowOrg !== JSON.stringify(targetRow)) {
          targetRow.updateFlag = true;
        }
        newSpreadsheetRecordData.splice(
          newSpreadsheetRecordData.length,
          0,
          targetRow,
        );
      } else {
        let newRow = JSON.parse(
          JSON.stringify(
            ProjectStakeholderListInitialState.spreadsheet.tmplateRow,
          ),
        );
        const roleCd = pageState.codes.roleCdList?.find(
          (e) => loadedRow["roleName"] === e.label,
        )?.value;
        const primFlg =
          pageState.codes.inDefCommonFlagTypeCd?.find(
            (e) => loadedRow["primFlg"] === e.label,
          )?.value ?? "";
        newRow.isAddRow = true;
        newRow.updateFlag = true;
        newRow.primFlg = primFlg;
        newRow.roleName = roleCd ? loadedRow["roleName"] : "";
        newRow.roleCd = roleCd ?? "";
        newRow.teamCd = loadedRow["teamCd"];
        newRow.userId = loadedRow["userId"];
        newRow.stkhldrNote = loadedRow["stkhldrNote"];
        newSpreadsheetRecordData.splice(
          newSpreadsheetRecordData.length,
          0,
          newRow,
        );
      }
    });

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });
  };

  const onClickAddRow = () => {
    const insertRowIndex: number =
      (pageState.spreadsheet.selectRowIdx ?? -1) + 1;

    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    const addSpreadsheetRow: SpreadsheetRecordData = {
      ...ProjectStakeholderListInitialState.spreadsheet.tmplateRow,
      isAddRow: true,
    };

    if (pageState.fromActionList) {
      addSpreadsheetRow.roleCd = pageState.fromActionListParam?.roleCd ?? "";
      addSpreadsheetRow.roleName =
        pageState.codes.roleCdList?.find(
          (e) => e.value === pageState.fromActionListParam?.roleCd,
        )?.label ?? "";
      addSpreadsheetRow.teamCd = pageState.fromActionListParam?.teamCd ?? "";
      addSpreadsheetRow.primFlg = spreadsheetState?.results.some(
        (e) =>
          e.roleCd === pageState.fromActionListParam?.roleCd &&
          e.teamCd === pageState.fromActionListParam?.teamCd,
      )
        ? InDefCommonFlagType.NO
        : InDefCommonFlagType.YES;
    }

    newSpreadsheetRecordData.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
    });
  };

  const onClickBack = () => {
    if (!spreadsheetState) return;

    if (!spreadsheetState?.results.some((e) => e.updateFlag)) {
      closeModal(false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false);
        },
      });
    }
  };

  const onClickReload = () => {
    if (!spreadsheetState) return;

    if (!spreadsheetState?.results.some((e) => e.updateFlag)) {
      requestReloadProjectInfo();
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          requestReloadProjectInfo();
        },
      });
    }
  };

  const onClickDelete = () => {
    const deleteProjectStakeholdeers = () => {
      resetUserError();

      const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
        ? [
            ...spreadsheetState.results.map((e) => {
              return {
                ...e,
                errorColumnList: [],
                errorMessage: "",
              };
            }),
          ]
        : [];

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRecordData],
      });

      const stakeholderList = spreadsheetState?.results
        .filter((e) => e.updateFlag)
        .map((e) => {
          return {
            stkhldrId: e.stkhldrId,
            primFlg: e.primFlg,
            roleCd: e.roleCd,
            teamCd: e.teamCd,
            userId: e.userId,
            index: spreadsheetState?.results.indexOf(e),
          };
        });

      const request = {
        pjtId: pageState.pjtId,
        pjtTypeCd: pageState.pjtTypeCd,
        stakeholderList: stakeholderList ?? [],
        updateDateTime: pageState.updateDateTime,
      };
      deleteProjectStakeholdersRequest(request);
    };

    showConfirmationDialog({
      title: "",
      message: "",
      type: "confirm",
      onOkButtonClicked: deleteProjectStakeholdeers,
    });
  };

  const onListenDeleteProjectStakeholdersResponse = (): void => {
    if (!deleteProjectStakeholdersResponse) return;

    if (Array.isArray(deleteProjectStakeholdersResponse)) {
      const currentSpreadsheetRecordData: SpreadsheetRecordData[] =
        spreadsheetState ? [...spreadsheetState.results] : [];
      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        deleteProjectStakeholdersResponse,
        currentSpreadsheetRecordData,
        ProjectStakeholderListInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRecordData],
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      requestReloadProjectInfo();
    }
  };
  useEffect(onListenDeleteProjectStakeholdersResponse, [
    deleteProjectStakeholdersResponse,
  ]);

  const onClickSave = () => {
    const updateProjectStakeholdeers = () => {
      resetUserError();

      const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
        ? [
            ...spreadsheetState.results.map((e) => {
              return {
                ...e,
                errorColumnList: [],
                errorMessage: "",
              };
            }),
          ]
        : [];

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRecordData],
      });

      const stakeholderList = spreadsheetState?.results
        .filter((e) => e.updateFlag)
        .map((e) => {
          return {
            stkhldrId: e.stkhldrId,
            primFlg: e.primFlg,
            roleCd: e.roleCd,
            teamCd: e.teamCd,
            userId: e.userId,
            stkhldrNote: e.stkhldrNote,
            index: spreadsheetState?.results.indexOf(e),
          };
        });

      const request = {
        pjtId: pageState.pjtId,
        pjtTypeCd: pageState.pjtTypeCd,
        stakeholderList: stakeholderList ?? [],
        updateDateTime: pageState.updateDateTime,
      };
      updateProjectStakeholdersRequest(request);
    };

    showConfirmationDialog({
      title: "",
      message: "",
      type: "confirm",
      onOkButtonClicked: updateProjectStakeholdeers,
    });
  };

  const onListenUpdateProjectStakeholdersResponse = (): void => {
    if (!updateProjectStakeholdersResponse) return;

    if (Array.isArray(updateProjectStakeholdersResponse)) {
      const currentSpreadsheetRecordData: SpreadsheetRecordData[] =
        spreadsheetState ? [...spreadsheetState.results] : [];
      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        updateProjectStakeholdersResponse,
        currentSpreadsheetRecordData,
        ProjectStakeholderListInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRecordData],
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      if (pageState.fromActionList) {
        closeModal(true);
      } else {
        requestReloadProjectInfo();
      }
    }
  };
  useEffect(onListenUpdateProjectStakeholdersResponse, [
    updateProjectStakeholdersResponse,
  ]);

  const InvalidRoleMessage: React.FC = () => {
    if (
      pageState.invalidRoleList === undefined ||
      pageState.invalidRoleList?.length < 1
    ) {
      return null;
    }
    const targetRoleList = pageState.invalidRoleList
      ?.map((e) => {
        return { roleCd: e.roleCd, roleName: e.roleName };
      })
      .filter((x, i, arr) => arr.findIndex((y) => y.roleCd === x.roleCd) === i);

    const invalidRoleList = targetRoleList.map((e) => {
      if (
        [
          RoleType.DIP_MANAGER,
          RoleType.PIC_OF_IF_RD,
          RoleType.PROJECT_APPROVER,
          RoleType.PROJECT_OWNER,
        ].includes(e.roleCd as RoleType)
      ) {
        return { label: e.roleName, value: "" };
      }
      const targetTeamCdList = pageState.invalidRoleList
        ?.filter((ee) => e.roleCd === ee.roleCd)
        .map((ee) => ee.teamCd)
        .join(", ");
      return { label: e.roleName, value: targetTeamCdList };
    });

    return (
      <>
        {invalidRoleList.length > 0 ? (
          <FrVrfSingleLineDisplayField
            style={{
              color: "red",
              "--vrf-single-line-display-field-font-size": "14px",
            }}
            firstValue={
              targetRoleList.some((e) =>
                [RoleType.DIP_APPROVER, RoleType.PIC_OF_DIP_APP].includes(
                  e.roleCd as RoleType,
                ),
              )
                ? "[ Required Role ] *DIP Approver and PIC of DIP App are automatically assigned at the time of IF requirement (re)determination."
                : "[ Required Role ]"
            }
          />
        ) : null}
        {invalidRoleList.map((e) => {
          return (
            <FrVrfSingleLineDisplayField
              style={{
                color: "red",
                "--vrf-single-line-display-field-font-size": "12px",
              }}
              firstValue={e.label}
              secondValue={e.value}
              labelPosition="top"
            />
          );
        })}
      </>
    );
  };

  return (
    <>
      {inputStakeholderDialogState.isShow && (
        <>
          <InputStakeholderDialogContent />
        </>
      )}
      <div>
        <FrVrfFormPanel columns={1}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginLeft: "30px",
              }}
            >
              <div style={{ width: "70%" }}>
                <InvalidRoleMessage />
              </div>
              <FrVrfButtonPanel align="right">
                <FrVrfActionList
                  icon="frIcon:question"
                  options={MANUAL_LINK_LIST}
                  onSelectedOption={(event: CustomEvent) => {
                    window.open(event.detail.value.value);
                  }}
                />
                <FrVrfFileUploadField
                  style={{ marginTop: "6px" }}
                  showConfirm={true}
                  uploadBtnText="Load"
                  accept=".xlsx"
                  onFileSelected={onClickLoad}
                  visible={!pageState.fromActionList}
                />
                <FrVrfButton
                  style={{ marginTop: "6px" }}
                  content="Unload"
                  theme="secondary"
                  size="small"
                  onClicked={onClickUnload}
                  visible={!pageState.fromActionList}
                />
                <FrVrfButton
                  style={{ marginTop: "6px" }}
                  content="Add Row"
                  theme="secondary"
                  size="small"
                  onClicked={onClickAddRow}
                />
              </FrVrfButtonPanel>
            </div>
            <FrVrfSpreadsheet
              data={spreadsheetInitialData}
              templateUrl={TEMPLATE_FILE_URL}
              maxHeight={-80}
              onInitialized={initSpreadsheetState}
              onCellEdited={onCellEditedHandle}
              onCellClicked={onCellClickedHandle}
              onCellSelected={onCellSelectedHandle}
              onCellBulkEdited={onCellBulkEdited}
            />
          </div>
        </FrVrfFormPanel>
        <FrVrfFooter>
          <FrVrfButton
            content={pageState.fromActionList ? "Cancel" : "Back"}
            theme="secondary"
            size="large"
            onClicked={onClickBack}
          />
          <FrVrfButton
            content="Reload"
            theme="primary"
            size="large"
            onClicked={onClickReload}
          />
          <FrVrfButton
            content="Delete"
            theme="primary"
            size="large"
            disabled={
              pageState.status.invalidList.length !== 0 ||
              pageState.status.invalidRecordList.length !== 0 ||
              !spreadsheetState?.results.some((e) => e.updateFlag)
            }
            onClicked={onClickDelete}
          />
          <FrVrfButton
            content={pageState.fromActionList ? "Save & Back" : "Save"}
            theme="primary"
            size="large"
            disabled={
              pageState.status.invalidList.length !== 0 ||
              pageState.status.invalidRecordList.length !== 0 ||
              !spreadsheetState?.results.some((e) => e.updateFlag)
            }
            onClicked={onClickSave}
          />
        </FrVrfFooter>
      </div>
    </>
  );
};
